import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import { getRoleRangesCm, sliceOlLineByCm } from "../../util";
import {
  distanceAlongFeatureCm,
  findCrosslinkRelationsAt,
} from "../crosslinkRelations";

// Endpoint-marker colours. Corresponding ends of the matched road and lane
// sections share a colour, so it's visible which end maps to which.
export const CROSSLINK_MATCH_START_COLOR = "#ffd400"; // lower-cm end of the match
export const CROSSLINK_MATCH_END_COLOR = "#7c4dff"; // higher-cm end of the match

export function getCrosslinkLayerHander(vizVectorSource) {
  return (feature, layer, position) => {
    const distanceAtFeatureCm = distanceAlongFeatureCm(feature, position);
    const featureId = feature.getId();
    const relationsOfFeature = findCrosslinkRelationsAt(
      feature,
      layer.getSource().getFeatures(),
      distanceAtFeatureCm,
    );
    if (relationsOfFeature.length > 0) {
      vizVectorSource.clear();
      // Unique suffix per drawn section so the viz source never sees duplicate
      // ids (OpenLayers throws on those) while keeping the original id prefix —
      // CrosslinkHoverLayer's isSD() only inspects the leading "6:".
      let sectionIndex = 0;
      const addSections = (srcFeature, ranges) => {
        const geometries =
          ranges.length === 0
            ? [srcFeature.getGeometry().clone()]
            : ranges.map((range) =>
                sliceOlLineByCm(
                  srcFeature.getGeometry(),
                  range.startCm,
                  range.endCm,
                ),
              );
        geometries.forEach((geometry) => {
          const sectionFeature = new Feature({ geometry });
          sectionFeature.setId(`${srcFeature.getId()}#${sectionIndex++}`);
          vizVectorSource.addFeature(sectionFeature);
        });
        return geometries;
      };
      const addMarker = (coordinate, color) => {
        const marker = new Feature({ geometry: new Point(coordinate) });
        marker.set("markerColor", color);
        marker.setId(`crosslink-marker#${sectionIndex++}`);
        vizVectorSource.addFeature(marker);
      };
      // Mark the matching ends of the road and lane sections. `opposite` (from
      // the relation's direction tag) decides which end pairs with which.
      const addEndpointMarkers = (geomA, geomB, opposite) => {
        addMarker(firstCoord(geomA), CROSSLINK_MATCH_START_COLOR);
        addMarker(
          opposite ? lastCoord(geomB) : firstCoord(geomB),
          CROSSLINK_MATCH_START_COLOR,
        );
        addMarker(lastCoord(geomA), CROSSLINK_MATCH_END_COLOR);
        addMarker(
          opposite ? firstCoord(geomB) : lastCoord(geomB),
          CROSSLINK_MATCH_END_COLOR,
        );
      };
      // Semi-transparent ribbon spanning the gap between the matched road and
      // lane sections — a visual cue of what maps to what. The ring walks A
      // start→end, then B back toward A's start: for `same` direction that
      // means B reversed (end→start); for `opposite`, B as-is.
      const addConnectingPolygon = (geomA, geomB, opposite, outOfBounds) => {
        const a = geomA.getCoordinates();
        const b = geomB.getCoordinates();
        const ring = opposite ? [...a, ...b] : [...a, ...b.slice().reverse()];
        ring.push(ring[0]);
        const polygon = new Feature({ geometry: new Polygon([ring]) });
        polygon.setId(`crosslink-polygon#${sectionIndex++}`);
        // Flags an out-of-bounds relation so the hover layer paints the ribbon
        // red instead of blue (CrosslinkHoverLayer reads this).
        polygon.set("outOfBounds", outOfBounds === true);
        vizVectorSource.addFeature(polygon);
      };
      relationsOfFeature.forEach((relation) => {
        const memberIdKeys = relation
          .getKeys()
          .filter(
            (key) => key.startsWith("relation-member-") && key.endsWith("-id"),
          );
        if (memberIdKeys.length === 0) {
          return false;
        }
        const hoveredMemberIdKey = memberIdKeys.filter(
          (memberIdKey) => relation.get(memberIdKey) === featureId,
        )[0];
        const otherMemberIdKey = memberIdKeys.filter(
          (memberIdKey) => relation.get(memberIdKey) !== featureId,
        )[0];
        if (otherMemberIdKey === undefined) {
          return;
        }
        const otherMemberId = relation.get(otherMemberIdKey);
        const otherRole = relation.get(
          otherMemberIdKey.replace("-id", "-role"),
        );
        const otherFeature = layer
          .getSource()
          .getFeatures()
          .filter((myFeature) => myFeature.getId() === otherMemberId)[0];
        if (otherFeature === undefined) {
          return;
        }
        // Only highlight the single section of the other member that matches the
        // section under the cursor. The role LR-encodes participating ranges
        // along each member's own geometry; the hovered member's range index
        // containing the cursor maps to the same index on the other member.
        const hoveredRanges = getRoleRangesCm(
          relation.get(hoveredMemberIdKey.replace("-id", "-role")),
        );
        const otherRanges = getRoleRangesCm(otherRole);
        const cursorIndex = hoveredRanges.findIndex(
          (range) =>
            distanceAtFeatureCm >= range.startCm &&
            distanceAtFeatureCm <= range.endCm,
        );
        let selectedRanges;
        if (otherRanges.length === 0) {
          selectedRanges = []; // no decodable range → whole geometry
        } else if (otherRanges.length === 1) {
          selectedRanges = otherRanges;
        } else if (cursorIndex >= 0 && cursorIndex < otherRanges.length) {
          selectedRanges = [otherRanges[cursorIndex]];
        } else {
          selectedRanges = otherRanges; // can't pair → fall back to all sections
        }
        // Highlight the hovered member's own section under the cursor too, so
        // both sides of the matched relation light up — not just the other map.
        const hoveredSelectedRanges =
          cursorIndex >= 0 ? [hoveredRanges[cursorIndex]] : hoveredRanges;
        const hoveredGeometries = addSections(feature, hoveredSelectedRanges);
        const otherGeometries = addSections(otherFeature, selectedRanges);
        const geomA = hoveredGeometries[0];
        const geomB = otherGeometries[0];
        if (geomA !== undefined && geomB !== undefined) {
          const opposite = isOpposite(
            getDirectionTag(relation),
            firstCoord(geomA),
            lastCoord(geomA),
            firstCoord(geomB),
            lastCoord(geomB),
          );
          // Out-of-bounds: a member's role runs past its own length. Detected
          // here too (not just in CrosslinkCoverageHandler) so the hover ribbon
          // can signal it in red.
          const outOfBounds =
            rangesOvershoot(hoveredRanges, feature.get("lengthCm")) ||
            rangesOvershoot(otherRanges, otherFeature.get("lengthCm"));
          addConnectingPolygon(geomA, geomB, opposite, outOfBounds);
          addEndpointMarkers(geomA, geomB, opposite);
        }
      });
    }
  };
}

// Whether any range ends past the member's own length — the relation is then
// out-of-bounds on that member. Open-ended ranges (Infinity end) mean "till the
// end of the segment" by design, so they don't count.
function rangesOvershoot(ranges, lengthCm) {
  return (
    lengthCm > 0 &&
    ranges.some(
      (range) => Number.isFinite(range.endCm) && range.endCm > lengthCm,
    )
  );
}

function firstCoord(geometry) {
  return geometry.getCoordinates()[0];
}

function lastCoord(geometry) {
  const coordinates = geometry.getCoordinates();
  return coordinates[coordinates.length - 1];
}

// The crosslink relation's mandatory `direction` tag (same|opposite). Falls
// back to a prefixed/namespaced key (e.g. "<layerId>-direction", ":direction")
// in case the WFS export renames it.
function getDirectionTag(relation) {
  const direct = relation.get("direction");
  if (direct !== undefined) {
    return direct;
  }
  const key = relation
    .getKeys()
    .find((candidate) => /(^|[-:])direction$/.test(candidate));
  return key ? relation.get(key) : undefined;
}

// Whether the two sections run in opposite directions. Prefer the crosslink
// `direction` tag; if it's missing/unknown, infer from geometry by choosing the
// endpoint pairing with the shorter total distance.
function isOpposite(direction, aStart, aEnd, bStart, bEnd) {
  const normalized =
    typeof direction === "string" ? direction.toLowerCase() : direction;
  if (normalized === "same") {
    return false;
  }
  if (normalized === "opposite") {
    return true;
  }
  const sameCost = distance(aStart, bStart) + distance(aEnd, bEnd);
  const oppositeCost = distance(aStart, bEnd) + distance(aEnd, bStart);
  return oppositeCost < sameCost;
}

function distance(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}
